import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import {
  Receipt, LayoutDashboard, FileText, CreditCard, LogOut, ChevronLeft, BarChart3, Tag, FileSpreadsheet, Menu, CalendarRange, Bell
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import NotificationBell from "@/components/NotificationBell";
import RealtimeStatusBadge from "@/components/RealtimeStatusBadge";
import MobileBottomNav from "@/components/MobileBottomNav";
import { useGlobalRealtimeSync } from "@/hooks/useGlobalRealtimeSync";
import SubscriptionAlertsBanner from "@/components/SubscriptionAlertsBanner";

const navItems = [
  { to: "/accountant", icon: LayoutDashboard, label: "لوحة التحكم", end: true },
  { to: "/accountant/invoices", icon: FileText, label: "فواتير الأطفال" },
  { to: "/accountant/subscription-invoices", icon: FileSpreadsheet, label: "فواتير الاشتراكات" },
  { to: "/accountant/monthly-status", icon: CalendarRange, label: "حالة الشهر" },
  { to: "/accountant/alerts-log", icon: Bell, label: "سجل التنبيهات" },
  { to: "/accountant/subscriptions", icon: CreditCard, label: "اشتراكات الحضانات" },
  { to: "/accountant/plan-pricing", icon: Tag, label: "أسعار الباقات" },
  { to: "/accountant/reports", icon: BarChart3, label: "التقارير المالية" },
];

const SidebarBody = ({ user, profile, signOut, navigate, roles, onNavigate }: any) => (
  <>
    <div className="p-4 border-b border-orange-700">
      <h1 className="text-lg font-bold flex items-center gap-2">
        <Receipt className="w-5 h-5 text-orange-300" />
        لوحة المحاسب
      </h1>
      <p className="text-xs text-orange-400 mt-1 truncate">{profile?.full_name || user?.email}</p>
    </div>

    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
              isActive
                ? "bg-orange-500/20 text-orange-200 font-medium"
                : "text-orange-300 hover:bg-orange-800 hover:text-white"
            )
          }
        >
          <item.icon className="w-4 h-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>

    <div className="p-3 border-t border-orange-700 space-y-2">
      {roles.includes("super_admin") && (
        <Button
          variant="ghost"
          className="w-full justify-start gap-2 text-orange-300 hover:text-white hover:bg-orange-800"
          onClick={() => { navigate("/admin"); onNavigate?.(); }}
        >
          <ChevronLeft className="w-4 h-4" />
          لوحة المشرف العام
        </Button>
      )}
      <Button
        variant="ghost"
        className="w-full justify-start gap-2 text-red-400 hover:text-red-300 hover:bg-red-900/20"
        onClick={signOut}
      >
        <LogOut className="w-4 h-4" />
        تسجيل خروج
      </Button>
    </div>
  </>
);

const AccountantLayout = () => {
  const { roles, loading, user, signOut, profile } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useGlobalRealtimeSync();

  const hasAccess = roles.includes("accountant") || roles.includes("super_admin");

  useEffect(() => {
    if (!loading && !hasAccess) {
      navigate("/dashboard");
    }
  }, [loading, hasAccess, navigate]);

  if (loading || !hasAccess) return null;

  return (
    <div className="min-h-screen flex" dir="rtl">
      <aside className="w-64 bg-orange-900 text-white hidden md:flex flex-col">
        <SidebarBody user={user} profile={profile} signOut={signOut} navigate={navigate} roles={roles} />
      </aside>

      <div className="flex-1 flex flex-col overflow-auto min-w-0">
        <header className="flex items-center justify-between gap-2 p-3 sm:p-4 border-b bg-background sticky top-0 z-20">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label="القائمة">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="p-0 w-72 bg-orange-900 text-white border-orange-700">
              <div className="flex flex-col h-full">
                <SidebarBody user={user} profile={profile} signOut={signOut} navigate={navigate} roles={roles} onNavigate={() => setOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>

          <h2 className="md:hidden text-sm font-semibold flex-1 text-center">المحاسب</h2>

          <div className="flex items-center gap-2">
            <RealtimeStatusBadge />
            <NotificationBell />
          </div>
        </header>
        <main className="app-main flex-1 p-4 sm:p-6 overflow-auto has-bottom-nav md:!pb-6">
          <SubscriptionAlertsBanner linkTo="/accountant/monthly-status" />
          <Outlet />
        </main>
      </div>

      <MobileBottomNav />
    </div>
  );
};

export default AccountantLayout;
